import { accountService, aggregateFieldService, formulaFieldService } from '@/services'
import { PageConfig, PropConnConfig } from '@/types/AccountServiceType'
import { AggregateFieldModel, FormulaContainer } from '@/types/FormulaFieldType'
import { OtherThemeSetting, ThemeEmailInfoSettings } from '@/types/ThemeSettingsType'
import { openContact } from '@/utils'
import axios from 'axios'
import Vue from 'vue'
import { RouteConfig } from 'vue-router'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
    state: {
        user: {
            username: null,
            firstName: '',
            lastName: '',
            title: '',
            initial: '',
            email: null,
            permissions: {},
            clients: [],
            defaultClient: '',
            accessToken: ''
        },
        authenticated: false,
        loading: false,
        loadingText: '',
        setting:{theme:{},jumpLinks:[],themeOther:{}},
        forbidden: false,
        appKey: Date.now(),
        cssProps: {
            '--border-radius': '28px',
            '--font-family': 'Roboto',
        } as Record<string, string> | null,
        help: {
            email:'',
            cc:'',
            subject:'',
            url:'',
            mode:'off',
        } as ThemeEmailInfoSettings,
        suggestion: {
            email:'',
            cc:'',
            subject:'',
            url:'',
            mode:'off',
        } as ThemeEmailInfoSettings,
        config: {} as PropConnConfig,
        aggregatefields: [] as AggregateFieldModel[],
        formulaFields: [] as FormulaContainer[],
        currentPageConfig: { 
            fields: [],
            defaultGroupBy: [],
            pageName: '',
            api: '',
            exportApi: { 
                api: '',
                requiredPermissions: []
            },
            showTotal: false,
            keywordSearchPlaceholder: '',
            enableEditor: false,
            enableAggFields: false,
            importables: [],
            pageAlias: '',
            enableMailMerge: false
        } as PageConfig,
        page: '',
        routes: [] as RouteConfig[]
    },
    mutations: {
        setUser(state, user) {
            if (user == null) {
                state.user = {
                    username: null,
                    firstName: '',
                    lastName: '',
                    title: '',
                    initial: '',
                    email: null,
                    permissions: {},
                    clients: [],
                    defaultClient: '',
                    accessToken: ''
                }
            } else {
                const tempUser = user

                tempUser.permissions = user.permissions.reduce((prev: Record<string, boolean>, curr: string) => {
                    prev[curr] = true
                    return prev
                }, {})
                state.user = tempUser
            }
        },
        setAuthenticated(state, payload) {
            state.authenticated = payload
        },
        setLoading(state, payload = false) {
            state.loading = payload
        },
        setLoadingText(state, payload = '') {
            state.loadingText = payload
        },
        setSetting(state,payload) {
            state.setting = payload
        },
        setIsForbidden(state, payload) {
            state.forbidden = payload
        },
        setAppKey(state, payload) {
            state.appKey = payload
        },
        setCssProps(state, payload) {
            state.cssProps = payload
        },
        setHelp(state, payload) {
            state.help = payload
        },
        setSuggestion(state, payload) {
            state.suggestion = payload
        },
        setConfig(state, payload) {
            state.config = payload
        },
        setAggregateFields(state, payload) {
            state.aggregatefields = payload
        },
        setFormulaFields(state, payload) {
            state.formulaFields = payload
        },
        setCurrentPageConfig(state,payload) {
            state.currentPageConfig = payload
        },
        setCurrentPage(state, payload) {
            state.page = payload
        },
        setRoutes(state, payload)  {
            state.routes = payload
        }
    },
    actions: {
        async logout({ commit }, redirect: boolean) {
            console.log('Logging out...')
            try {
                commit('setLoading', true)
                try {
                    const logout = await accountService.Logout()
                    commit('setUser', null)
                    commit('setAuthenticated', false)
                    if(redirect) {
                        const postLoginUri =  `${window.location.origin}/?domain_hint=none`
                        window.location.href = `${logout.headers.location}?post_logout_redirect_uri=${encodeURIComponent(postLoginUri)}`
                    }
                } finally {
                    commit('setLoading', false)
                }

            } catch (e) {
                console.log('logout error...', e)
            }

            console.log('logout succesfully...')
        },
        async getUserInfo({ dispatch, commit }) {
            try {
                this.commit('setLoading', true)
                const user = await accountService.GetUser()

                dispatch('updateUser', user)
            } catch (err) {
                if(axios.isAxiosError(err) && err.response && err.response.status == 500){
                    // Try again
                    dispatch('getUserInfo')
                }
                commit('setUser', null)
                commit('setAuthenticated', false)
            } finally {
                this.commit('setLoading', false)
            }
        },
        async getAggregateFields({ commit, state }) {
            commit('setAggregateFields', (await aggregateFieldService.getAll(state.page)).records)
        },
        async getFormulaFields({ commit, state }) {
            commit('setFormulaFields', (await formulaFieldService.getAll(state.page)).records)
        },
        async getCurrentPageConfig({ commit, state }) {
            commit('setCurrentPageConfig', (await accountService.GetPageConfig(state.page)))
        },
        updateUser({ commit }, user: any) {
            commit('setAuthenticated', true)
            commit('setUser', user)
        },
        showPageLoading({ commit }, show = true, loadingText = 'Loading please wait...') {
            commit('setLoadingText', loadingText)
            commit('setLoading', show)
        },
        forbidUser({ commit }, forbid = true) {
            commit('setIsForbidden', forbid)
        },
        generateNewKey({ commit}) {
            commit('setAppKey', Date.now())
        },
        updateOtherTheme({ commit }, otherTheme: OtherThemeSetting) {
            commit('setCssProps', {
                '--border-radius': `${otherTheme.radius}px`,
                '--font-family': otherTheme.font,
            })

            if(otherTheme.help) {
                commit('setHelp', otherTheme.help)
            }


            if(otherTheme.suggestion)  {
                commit('setSuggestion', otherTheme.suggestion)
            }
        },
        updateCurrentPage({ commit}, page: string) {
            commit('setCurrentPage', page)
        },
        updateRoutes({ commit}, routes: Array<RouteConfig>) {
            commit('setRoutes', routes)
        },
        async getConfig({ commit}) {
            try {
                commit('setLoading', true)
                const config = await accountService.GetConfig()

                commit('setConfig', config)
            } catch (err) {
                commit('setConfig', {})
            } finally {
                commit('setLoading', false)
            }
        },
        onHelp({ state }) {
            openContact(state.help)
        },
        onSuggestion({ state }) {
            openContact(state.suggestion)
        }
    },
    getters: {
        isAuthenticated: state => state.authenticated,
        isForbidden: state => state.forbidden,
        user: state => state.user,
        appKey: state=> state.appKey,
        cssProps: state => state.cssProps,
        help: state => state.help,
        suggestion: state => state.suggestion,
        config: state => state.config,
        aggregateFields: state => state.aggregatefields,
        formulaFields: state => state.formulaFields,
        currentPageFields: state => state.currentPageConfig.fields,
        currentPageConfig: state => state.currentPageConfig,
        aggregateFieldLabelsLookup: state => state.aggregatefields.reduce((prev, curr) =>{
            prev[curr.fieldLabel] = true
            return prev
        }, {} as Record<string, boolean>),
        formulaFieldLabelsLookup: state => state.formulaFields.reduce((prev, curr) =>{
            prev[curr.fieldLabel] = true
            return prev
        }, {} as Record<string, boolean>),
        // This is needed to distingiush if the field to check is an external field
        currentPageConfigExternalFields: state => state.currentPageConfig.fields.reduce((prev, curr) => {
            if(curr.isExternalField && curr.alias && curr.externalFieldType === 'AggregateField') {
                prev.aggregateFields[curr.alias] = true
            } else if(curr.isExternalField && curr.alias && curr.externalFieldType === 'FormulaField') {
                prev.formulaFields[curr.alias] = true
            }
            return prev
        }, {aggregateFields : {}, formulaFields: {}, } as  {aggregateFields: Record<string, boolean>, formulaFields: Record<string, boolean>}),
        page: state => state.page,
        pageAlias: state => state.currentPageConfig.pageAlias,
    },
    modules: {
    }
})
